import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { buildXlsx, CellValue } from '../xlsx-writer';
import { formatFyYear } from '../fy-year.pipe';

/** One record that failed to reach the blockchain. */
export interface FailureRecord {
    /** validation | recovery | unprocessed | duplicate */
    category: string;
    categoryLabel: string;
    voucherNumber: string | null;
    uddi: string | null;
    documentId: number | null;
    reason: string;
    reasonCode: string;
    step: string | null;
    fileUrl: string | null;
    filename: string | null;
    documentTypeCode: string | null;
    fyYear: string | null;
    attemptNo: number | null;
    maxAttempts: number | null;
    failedAt: string;
    source: string;
}

/**
 * A record the recovery scheduler FIXED: it was failing, the 02:00 run retried
 * it, and the retry succeeded. Reported beside the failures, never inside them.
 */
export interface RecoveredRecord {
    voucherNumber: string | null;
    uddi: string | null;
    documentId: number;
    transactionId: string;
    blockNumber: number | null;
    attempts: number;
    documentTypeCode: string | null;
    categoryCode: string | null;
    filename: string | null;
    fyYear: string | null;
    fileUrl: string | null;
    uploadedAt: string;
    recoveredAt: string;
}

export interface FailureReport {
    success: boolean;
    /** A YYYY-MM-DD date, or 'all'. */
    scope: string;
    generatedAt: string;
    total: number;
    counts: { [category: string]: number };
    categoryLabels: { [category: string]: string };
    truncated: boolean;
    records: FailureRecord[];
    recoveredCount: number;
    recovered: RecoveredRecord[];
}

/** One recent day with failures and/or scheduler recoveries. */
export interface FailureDay {
    date: string;
    failures: number;
    recovered: number;
}

/**
 * Older failures, folded into a month. `from`/`to` are the first and last day
 * in the month that actually has failures, and are handed straight back to the
 * report endpoint as an inclusive range.
 */
export interface FailureMonth {
    month: string;   // YYYY-MM
    failures: number;
    recovered: number;
    from: string;
    to: string;
}

export interface FailureSummary {
    /**
     * Everything still failing — the badge number. It is the SUM of `days` and
     * `months`, so the badge can never disagree with the rows in the menu.
     */
    openTotal: number;
    /** Records the scheduler fixed. Not part of openTotal or the badge. */
    recoveredTotal: number;
    recentDays: number;
    days: FailureDay[];
    months: FailureMonth[];
}

/** What a report covers: everything, one day, one month, or a custom range. */
export type ReportScope =
    | { kind: 'all' }
    | { kind: 'day'; date: string }
    | { kind: 'month'; month: string; from: string; to: string }
    | { kind: 'range'; from: string; to: string };

/**
 * Reads blockchain upload failures and turns them into an Excel workbook.
 *
 * The API only returns JSON — the .xlsx is assembled here in the browser (see
 * `shared/xlsx-writer.ts`), so there is no server-side download endpoint.
 */
@Injectable({
    providedIn: 'root'
})
export class BlockchainFailureReportService {
    private apiUrl = `${environment.apiUrl}/ethereum/failure-report`;

    constructor(private http: HttpClient) {}

    /**
     * Per-day counts + the open total. Resolves to an empty summary on error:
     * this feeds a toolbar menu, which must never break the rest of the header
     * (e.g. for a user without the export privilege).
     */
    getSummary(recentDays = 7): Observable<FailureSummary> {
        return this.http.get<FailureSummary>(`${this.apiUrl}/summary`, {
            headers: this.getAuthHeaders(),
            params: new HttpParams().set('recentDays', String(recentDays))
        }).pipe(
            map(res => ({
                openTotal: res?.openTotal || 0,
                recoveredTotal: res?.recoveredTotal || 0,
                recentDays: res?.recentDays || recentDays,
                days: res?.days || [],
                months: res?.months || []
            })),
            catchError(() => of({
                openTotal: 0,
                recoveredTotal: 0,
                recentDays,
                days: [] as FailureDay[],
                months: [] as FailureMonth[]
            }))
        );
    }

    /** Failure rows for one bucket of the summary. */
    getReport(scope: ReportScope): Observable<FailureReport> {
        let params = new HttpParams();
        if (scope.kind === 'all') {
            params = params.set('all', 'true');
        } else if (scope.kind === 'day') {
            params = params.set('date', scope.date);
        } else {
            // month and range both map to the endpoint's inclusive from/to.
            params = params.set('from', scope.from).set('to', scope.to);
        }

        return this.http.get<FailureReport>(this.apiUrl, {
            headers: this.getAuthHeaders(),
            params
        });
    }

    /** Stable key for a scope — used to track which row is downloading. */
    scopeKey(scope: ReportScope): string {
        if (scope.kind === 'all') return 'all';
        if (scope.kind === 'day') return `day:${scope.date}`;
        if (scope.kind === 'month') return `month:${scope.month}`;
        return `range:${scope.from}:${scope.to}`;
    }

    /**
     * Build the workbook. Voucher number and reason come first — they are what
     * the report exists to communicate; the rest is context for chasing a row.
     */
    buildWorkbook(report: FailureReport): Blob {
        const header: CellValue[] = [
            'S.No', 'Voucher Number', 'Failure Reason', 'Failure Type', 'Reason Code',
            'Attempt', 'UDDI', 'Voucher ID', 'Voucher Type', 'File Name',
            'FY Year', 'File URL', 'Failed On'
        ];

        const rows: CellValue[][] = [header];
        (report.records || []).forEach((r, i) => {
            rows.push([
                i + 1,
                r.voucherNumber || '',
                r.reason || '',
                r.categoryLabel || r.category || '',
                r.reasonCode || '',
                r.attemptNo && r.maxAttempts ? `${r.attemptNo} of ${r.maxAttempts}` : (r.attemptNo || ''),
                r.uddi || '',
                r.documentId || '',
                r.documentTypeCode || '',
                r.filename || '',
                formatFyYear(r.fyYear),
                r.fileUrl || '',
                this.formatDateTime(r.failedAt)
            ]);
        });

        const summaryRows: CellValue[][] = [
            ['Field', 'Value'],
            ['Report Scope', report.scope === 'all' ? 'All open failures' : report.scope],
            ['Total Failed Records', report.total || 0]
        ];
        Object.keys(report.counts || {}).forEach(key => {
            summaryRows.push([
                report.categoryLabels?.[key] || key,
                report.counts[key]
            ]);
        });
        summaryRows.push(['Recovered by recovery scheduler', report.recoveredCount || 0]);
        summaryRows.push(['Generated At', this.formatDateTime(report.generatedAt)]);
        if (report.truncated) {
            summaryRows.push(['Note', 'Row limit reached — this list is partial']);
        }

        // Successes get their own sheet. Mixing them into "Failed Records"
        // would defeat the point of a failure report.
        const recoveredHeader: CellValue[] = [
            'S.No', 'Voucher Number', 'Recovered On', 'Attempts Taken',
            'Transaction ID', 'Block Number', 'UDDI', 'Voucher ID',
            'Voucher Type', 'File Name', 'FY Year', 'Uploaded On'
        ];
        const recoveredRows: CellValue[][] = [recoveredHeader];
        (report.recovered || []).forEach((r, i) => {
            recoveredRows.push([
                i + 1,
                r.voucherNumber || '',
                this.formatDateTime(r.recoveredAt),
                r.attempts || '',
                r.transactionId || '',
                r.blockNumber || '',
                r.uddi || '',
                r.documentId || '',
                r.documentTypeCode || '',
                r.filename || '',
                formatFyYear(r.fyYear),
                this.formatDateTime(r.uploadedAt)
            ]);
        });

        return buildXlsx([
            {
                name: 'Failed Records',
                rows,
                colWidths: [6, 22, 70, 30, 22, 10, 34, 12, 18, 30, 10, 55, 20]
            },
            {
                name: 'Recovered',
                rows: recoveredRows,
                colWidths: [6, 22, 20, 14, 68, 14, 34, 12, 18, 30, 10, 20]
            },
            { name: 'Summary', rows: summaryRows, colWidths: [34, 46] }
        ]);
    }

    /** e.g. blockchain-failures-2026-09-03.xlsx */
    fileNameFor(scope: ReportScope): string {
        if (scope.kind === 'all') return `blockchain-failures-all-open-${this.today()}.xlsx`;
        if (scope.kind === 'day') return `blockchain-failures-${scope.date}.xlsx`;
        if (scope.kind === 'month') return `blockchain-failures-${scope.month}.xlsx`;
        return scope.from === scope.to
            ? `blockchain-failures-${scope.from}.xlsx`
            : `blockchain-failures-${scope.from}_to_${scope.to}.xlsx`;
    }

    /** Date -> YYYY-MM-DD in the viewer's own timezone (never via toISOString). */
    toDateString(d: Date): string {
        const p = (n: number) => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
    }

    /** "2026-09-03 09:33:45" in the viewer's own timezone. */
    private formatDateTime(value: string): string {
        if (!value) return '';
        const d = new Date(value);
        if (isNaN(d.getTime())) return String(value);
        const p = (n: number) => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} `
            + `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
    }

    private today(): string {
        const d = new Date();
        const p = (n: number) => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
    }

    private getAuthHeaders(): HttpHeaders {
        const token = localStorage.getItem('token');
        return new HttpHeaders({
            'Authorization': `Bearer ${token}`
        });
    }
}
